import styles from 'css/components/blue-button.module.css'
import caretRightIcon from 'img/caret-right.svg'
import commentIcon from 'img/dashicons-admin-comments.svg'
import downloadWhiteIcon from 'img/dashicons-download-white.svg'
import reverseMarkIcon from 'img/reverse-mark.svg'
import refreshWhiteIcon from 'img/refresh-white.svg'
import React from 'react'

interface BlueButtonProps {
    disabled?: boolean;
    visible?: boolean;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    label?: React.ReactNode;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
    type?: 'button' | 'submit' | 'reset';
    /** Additive, optional -- lets a caller layer a scoped CSS-module class
        (e.g. a toolbar's own "this button sits on an azure bar, not the
        default page background" variant) on top of the base look without
        touching every other BlueButton call site. */
    className?: string;
}

export const BlueButton: React.FC<BlueButtonProps> = ({
    disabled = false,
    visible = true,
    onClick,
    label,
    icon,
    iconPosition = 'left',
    type = 'button',
    className = '',
}) => {
    return (
        <button
            type={type}
            className={`${styles.blueButton} ${className}`}
            onClick={onClick}
            disabled={disabled}
            style={{ visibility: visible ? 'visible' : 'hidden' }}
        >
            {icon && iconPosition === 'left' && <span className={styles.blueButton__icon}>{icon}</span>}
            {label && label}
            {icon && iconPosition === 'right' && <span className={styles.blueButton__icon}>{icon}</span>}
        </button>
    )
}

export const DoneButton = ({disabled, onClick, type, className}: {disabled?: boolean, onClick?: () => void, type?: 'button' | 'submit' | 'reset', className?: string}) => {
    return (
        <BlueButton label="Done" onClick={onClick || (() => {})} disabled={disabled} type={type} className={className} />
    )
}

export const ClassButton = ({onClick}: {onClick: () => void}) => {
    return (
        <BlueButton label="Class" onClick={onClick} />
    )
}

/* Inline rather than an <img> import -- matches the mockup's own
   person-add SVG exactly (AllForOne_ClassroomDetail_v2.html's
   #addStudentBtn), same reasoning as footer.js's inlined social icons:
   this is a line-stroke icon meant to take BlueButton's label colour via
   currentColor, not a separate asset. */
const AddStudentIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <line x1="19" y1="8" x2="19" y2="14" />
        <line x1="22" y1="11" x2="16" y2="11" />
    </svg>
)

/* Terracotta, not the shared azure: Classroom Detail is the one Elevate screen
   whose mockup defines its own primary button (`.btn.btn-solid`). See the
   `.solidTerracotta` comment in blue-button.module.css for why this is hers
   and not our drift. */
export const AddStudentButton = ({onClick}: {onClick: () => void}) => {
    return (
        <BlueButton label="Add student" onClick={onClick} icon={<AddStudentIcon />} className={styles.solidTerracotta} />
    )
}

/* Her `#classActions` kebab (AllForOne_ClassroomDetail_v2.html): icon only,
   outline, 47x41, with the vertical three-dot mark inlined so it takes the
   button's own colour through the hover state. aria-haspopup/aria-expanded
   are hers too -- an icon-only control with no accessible name is unusable
   with a screen reader, so the label is not optional here. */
export const ClassActionsButton = ({onClick, expanded}: {onClick: () => void, expanded: boolean}) => {
    return (
        <button
            type="button"
            className={`${styles.blueButton} ${styles.outlineIcon}`}
            onClick={onClick}
            aria-haspopup="true"
            aria-expanded={expanded}
            aria-label="Class actions"
        >
            <span className={styles.blueButton__icon}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="19" r="1" />
                </svg>
            </span>
        </button>
    )
}

export const DownloadButton = ({onClick}: {onClick: () => void}) => {
    return (
        <BlueButton label="Download" onClick={onClick} icon={<img src={downloadWhiteIcon} alt="" />} />
    )
}

export const ReverseMarkButton = ({onClick, visible}: {onClick: () => void, visible?: boolean}) => {
    return (
        <div className={styles.reverseMarkButton}>
            <BlueButton 
                label={
                    <span>
                        REVERSE<br />
                        MARK
                    </span>
                }
                onClick={onClick} 
                visible={visible} 
                icon={<img src={reverseMarkIcon} alt="" />}
            />
        </div>
    )
}

/* No label on this one, so the icon IS the button's name and must describe
   the action rather than be decorative like the others above. */
export const CommentButton = ({onClick, visible}: {onClick: () => void, visible?: boolean}) => {
    return (
        <BlueButton onClick={onClick} icon={<img src={commentIcon} alt="Comment" />} visible={visible} />
    )
}

export const PreviousQuestionButton = ({onClick, visible}: {onClick: () => void, visible?: boolean}) => {
    return (
        <BlueButton label="Previous Question" onClick={onClick} iconPosition="left" visible={visible} />
    )
}

export const NextQuestionButton = ({onClick, visible}: {onClick: () => void, visible?: boolean}) => {
    return (
        <BlueButton label="Next Question" onClick={onClick} icon={<img src={caretRightIcon} alt="" />} iconPosition="right" visible={visible} />
    )
}

export const GetNewQuestionButton = ({onClick}: {onClick: () => void}) => {
    return (
        <BlueButton label="Get New Question" onClick={onClick} icon={<img src={refreshWhiteIcon} alt="" />} />
    )
}

export const NextSlideButton = ({onClick, className}: {onClick: () => void, className?: string}) => {
    return (
        <BlueButton label="Next slide" onClick={onClick} iconPosition="right" className={className} />
    )
}

/* The mockup's #nextLabel becomes "Finish lesson" on the final slide rather
   than a separate "Done" control, so this is the same button in its last
   state -- not a different one. DoneButton is left alone for its own callers. */
export const FinishLessonButton = ({onClick, className}: {onClick: () => void, className?: string}) => {
    return (
        <BlueButton label="Finish lesson" onClick={onClick} className={className} />
    )
}

export const PreviousSlideButton = ({onClick, className}: {onClick: () => void, className?: string}) => {
    return (
        <BlueButton label="Previous slide" onClick={onClick} iconPosition="left" className={className} />
    )
}

export const RegenerateQuestionsButton = ({onClick, disabled}: {onClick: () => void, disabled?: boolean}) => {
    return (
        <BlueButton label="Regenerate Questions" onClick={onClick} disabled={disabled} icon={<img src={refreshWhiteIcon} alt="" />} />
    )
}

export const CopyLessonButton = ({onClick, disabled}: {onClick: () => void, disabled?: boolean}) => {
    return (
        <BlueButton label="Copy Lesson" onClick={onClick} disabled={disabled} />
    )
}